import React, { createElement, useState } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { IconSizes, LockFullIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { AnimatePresence, motion } from 'motion/react'
import { BadgeCount } from 'shared/components/BadgeCount/BadgeCount'
import { useCommonStore } from 'shared/stores/commonStore'
import { useTopDockStore } from 'shared/stores/topDockStore'
import { useUnreadCountStore } from 'shared/stores/unreadCountStore'
import { IframeAppInstance } from 'shared/types/index'

import { useAppButtonState } from '../../hooks/useAppButtonState'
import { AppIcon } from '../AppIcon'
import styles from './TopDockButton.module.scss'
import { useRichAppIconObserver } from './useRichAppIconObserver'

interface TopDockButtonProps {
  app: IframeAppInstance
  onClick?: () => void
  index: number
  onLabelAnimationComplete?: () => void
  isLoading?: boolean
  isLocked?: boolean
  closeContextMenu?: () => void
}

export const TopDockButton: React.FC<TopDockButtonProps> = ({
  app,
  onClick,
  index,
  onLabelAnimationComplete,
  isLoading,
  isLocked,
  closeContextMenu
}) => {
  const buttonState = useAppButtonState(app)
  const isRichAppIconEnabled = !useTopDockStore(state =>
    state.isRichAppIconDisabled(app.id)
  )
  const { richAppIconData, setRichAppIconData } = useRichAppIconObserver({
    appId: app.id
  })
  const { apps } = useAvailableApps()

  const { isExpanded, isMinimized, isFocused } = buttonState
  const [isHovered, setIsHovered] = useState(false)
  const isDetached = useCommonStore(state => state.isDetached)

  const hasRichAppIcon = Boolean(app.richAppIcon)
  const hasRichAppIconData = Boolean(richAppIconData)
  const hasRichContent = Boolean(
    richAppIconData?.title ||
      richAppIconData?.description ||
      richAppIconData?.rightIcon
  )
  const showRichAppIcon = hasRichAppIconData && isRichAppIconEnabled

  const config = apps[app.id as AppId]
  const unreadCount = useUnreadCountStore(state =>
    state.getCountForApp(app.id, config?.slug)
  )
  const showFallbackLabel =
    (isFocused || (isDetached && isExpanded)) &&
    !(hasRichAppIcon && isRichAppIconEnabled) &&
    !hasRichAppIconData

  const RichAppIconComponent = app.richAppIcon

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick && !isLocked) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div
      className={styles.appButtonWrapper}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      <motion.div
        initial={{
          scale: 0,
          opacity: 0,
          y: 20
        }}
        animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }}
        transition={{
          delay: index * 0.05
        }}
        whileTap={isLoading || isLocked ? undefined : { scale: 0.95 }}
        onClick={isLoading || isLocked ? undefined : onClick}
        onKeyDown={isLoading ? undefined : handleKeyDown}
        role='button'
        tabIndex={isLocked || isLoading ? -1 : 0}
        aria-label={app.label}
        aria-disabled={isLocked}
        aria-busy={isLoading}
        className={cn(styles.iconContainer, {
          [styles.active]: isExpanded,
          [styles.loading]: isLoading,
          active: !isLoading && (isExpanded || isHovered || isFocused),
          [styles.locked]: isLocked
        })}
      >
        {isMinimized && <span className={styles.activeIndicator} />}

        {!isLoading && !isLocked && (
          <BadgeCount count={unreadCount} className={styles.badgeTopRight} />
        )}

        {RichAppIconComponent &&
          createElement(RichAppIconComponent, {
            setRichAppIconData,
            closeContextMenu
          })}

        <motion.div className={styles.iconWrapper}>
          {isLocked && !isLoading && (
            <span className={styles.lockBadge} aria-hidden>
              <LockFullIcon
                className={styles.lockIcon}
                size={IconSizes.Size20}
                color='var(--grey-800)'
              />
            </span>
          )}

          {showRichAppIcon && richAppIconData?.leftIcon ? (
            <span className={styles.leftIcon}>{richAppIconData.leftIcon}</span>
          ) : (
            <AppIcon app={app} width={24} height={24} className={styles.icon} />
          )}

          {isLoading && <div className={styles.shimmerOverlay} />}
        </motion.div>

        <AnimatePresence>
          {showRichAppIcon && richAppIconData && hasRichContent && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 'auto' }}
              exit={{ width: 0, transition: { duration: 0 } }}
              className={styles.richAppIcon}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className={styles.richAppIcon}
              >
                {(richAppIconData.title ||
                  richAppIconData.description ||
                  richAppIconData.rightIcon) && (
                  <span className={styles.separator} />
                )}

                {(richAppIconData.title || richAppIconData.description) && (
                  <div className={styles.textContainer}>
                    {richAppIconData.title && (
                      <span
                        className={styles.title}
                        style={{
                          color: `var(--rich-app-icon-color, ${richAppIconData.title.color})`,
                          fontWeight: richAppIconData.title.weight ?? undefined
                        }}
                      >
                        {richAppIconData.title.text}
                      </span>
                    )}
                    {richAppIconData.description && (
                      <span
                        className={styles.description}
                        style={{
                          color: `var(--rich-app-icon-color, ${richAppIconData.description.color})`,
                          fontWeight:
                            richAppIconData.description.weight ?? undefined
                        }}
                      >
                        {richAppIconData.description.text}
                      </span>
                    )}
                  </div>
                )}

                {richAppIconData.rightIcon && (
                  <span className={styles.rightIcon}>
                    {richAppIconData.rightIcon}
                  </span>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showFallbackLabel && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 'auto' }}
              exit={{ width: 0, transition: { duration: 0 } }}
              className={styles.label}
              onAnimationComplete={() => {
                onLabelAnimationComplete?.()
              }}
            >
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {app.label}
              </motion.span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}
